/**
 * ChatPescadilloConfigPage — Configuración runtime del chat IA "Pescadillo".
 *
 * Lee y escribe en `app_config` con prefijo `chat_pescadillo.*`:
 *   - enabled                    (bool)
 *   - provider                   ('azure_openai' | 'anthropic')
 *   - model_deployment           (string; nombre del deployment Azure OpenAI o
 *                                 model id de Anthropic)
 *   - daily_limit_messages       (int)
 *   - minute_limit_messages      (int)
 *   - max_history_messages       (int)
 *   - max_response_tokens        (int)
 *   - max_tool_iterations        (int)
 *   - system_prompt_version      (string; informativo)
 *
 * Muestra resumen de uso del día con `chat_pescadillo_admin_usage_summary`.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    MessageSquare, ToggleLeft, ToggleRight, Loader2, AlertTriangle,
    CheckCircle, BarChart3, Bot, Save, Cpu,
} from 'lucide-react';

// ─── Tipos ─────────────────────────────────────────────────────────────────────

type ProviderName = 'azure_openai' | 'anthropic';

interface ChatConfig {
    enabled: boolean;
    provider: ProviderName;
    modelDeployment: string;
    dailyLimitMessages: number;
    minuteLimitMessages: number;
    maxHistoryMessages: number;
    maxResponseTokens: number;
    maxToolIterations: number;
    systemPromptVersion: string;
}

interface UsageRow {
    day_utc: string;
    active_users: number;
    total_messages: number;
    total_tokens_in: number | string;
    total_tokens_out: number | string;
    by_provider: Record<string, { messages: number; tokens_in: number; tokens_out: number }>;
}

// ─── Defaults (espejo del backend en config-loader.ts) ─────────────────────────

const DEFAULTS: ChatConfig = {
    enabled: true,
    provider: 'azure_openai',
    modelDeployment: 'gpt-4o-mini',
    dailyLimitMessages: 30,
    minuteLimitMessages: 5,
    maxHistoryMessages: 12,
    maxResponseTokens: 1024,
    maxToolIterations: 5,
    systemPromptVersion: 'v1',
};

const PROVIDER_OPTIONS: Array<{ value: ProviderName; label: string; hint: string }> = [
    { value: 'azure_openai', label: 'Azure OpenAI', hint: 'Coste bajo, integración nativa Azure (UAMI). Modelo recomendado: gpt-4o-mini.' },
    { value: 'anthropic',    label: 'Anthropic Claude', hint: 'Mayor calidad de razonamiento. Modelo: claude-sonnet-4-20250514. Coste superior.' },
];

const MODEL_SUGGESTIONS: Record<ProviderName, string[]> = {
    azure_openai: ['gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini', 'gpt-5.4-nano'],
    anthropic:    ['claude-sonnet-4-20250514', 'claude-opus-4-20250514'],
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function coerceBool(v: unknown, fallback: boolean): boolean {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
        const norm = v.toLowerCase().trim();
        if (norm === 'true') return true;
        if (norm === 'false') return false;
    }
    return fallback;
}

function coerceInt(v: unknown, fallback: number): number {
    const n = Number(v);
    return Number.isFinite(n) ? Math.round(n) : fallback;
}

function coerceString(v: unknown, fallback: string): string {
    return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

function isProviderName(v: unknown): v is ProviderName {
    return v === 'azure_openai' || v === 'anthropic';
}

// ─── Componente ────────────────────────────────────────────────────────────────

export function ChatPescadilloConfigPage() {
    const queryClient = useQueryClient();

    // Estado local del form (se sincroniza con la query inicial)
    const [draft, setDraft] = useState<ChatConfig>(DEFAULTS);
    const [dirtyKeys, setDirtyKeys] = useState<Set<keyof ChatConfig>>(new Set());

    // ─── Carga config ─────────────────────────────────────────────────────────
    const { data: config, isLoading } = useQuery({
        queryKey: ['admin', 'chat-pescadillo-config'],
        queryFn: async (): Promise<ChatConfig> => {
            const { data, error } = await dbClient.rpc<Array<{ key: string; value: unknown }>>('admin_get_all_config');
            if (error) throw error;
            const items = normalizeRpcResponse(data) as Array<{ key: string; value: unknown }>;
            const map: Record<string, unknown> = {};
            for (const it of items) {
                if (it.key.startsWith('chat_pescadillo.')) map[it.key] = it.value;
            }
            const provider = map['chat_pescadillo.provider'];
            return {
                enabled:             coerceBool(map['chat_pescadillo.enabled'], DEFAULTS.enabled),
                provider:            isProviderName(provider) ? provider : DEFAULTS.provider,
                modelDeployment:     coerceString(map['chat_pescadillo.model_deployment'], DEFAULTS.modelDeployment),
                dailyLimitMessages:  coerceInt(map['chat_pescadillo.daily_limit_messages'], DEFAULTS.dailyLimitMessages),
                minuteLimitMessages: coerceInt(map['chat_pescadillo.minute_limit_messages'], DEFAULTS.minuteLimitMessages),
                maxHistoryMessages:  coerceInt(map['chat_pescadillo.max_history_messages'], DEFAULTS.maxHistoryMessages),
                maxResponseTokens:   coerceInt(map['chat_pescadillo.max_response_tokens'], DEFAULTS.maxResponseTokens),
                maxToolIterations:   coerceInt(map['chat_pescadillo.max_tool_iterations'], DEFAULTS.maxToolIterations),
                systemPromptVersion: coerceString(map['chat_pescadillo.system_prompt_version'], DEFAULTS.systemPromptVersion),
            };
        },
    });

    useEffect(() => {
        if (config) {
            setDraft(config);
            setDirtyKeys(new Set());
        }
    }, [config]);

    // ─── Carga uso 7 días ──────────────────────────────────────────────────────
    const { data: usage = [], isLoading: usageLoading } = useQuery({
        queryKey: ['admin', 'chat-pescadillo-usage', 7],
        queryFn: async (): Promise<UsageRow[]> => {
            const { data, error } = await dbClient.rpc<UsageRow[]>('chat_pescadillo_admin_usage_summary', {
                p_days: 7,
            });
            if (error) throw error;
            return normalizeRpcResponse(data) as UsageRow[];
        },
    });

    const totals = useMemo(() => {
        let messages = 0;
        let tokensIn = 0;
        let tokensOut = 0;
        const usersSet = new Set<string>(); // active_users ya viene por día agregado, no podemos sumar usuarios únicos cross-día sin más detalle
        for (const row of usage) {
            messages += Number(row.total_messages ?? 0);
            tokensIn += Number(row.total_tokens_in ?? 0);
            tokensOut += Number(row.total_tokens_out ?? 0);
            usersSet.add(`${row.day_utc}:${row.active_users}`);
        }
        const todayRow = usage[0];
        return {
            messages,
            tokensIn,
            tokensOut,
            todayMessages: Number(todayRow?.total_messages ?? 0),
            todayUsers: Number(todayRow?.active_users ?? 0),
        };
    }, [usage]);

    // ─── Mutación: upsert una clave de chat_pescadillo ─────────────────────────
    const upsertMutation = useMutation({
        mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
            const { error } = await dbClient.rpc('admin_upsert_app_config', {
                p_key: key,
                p_value: value,
            });
            if (error) throw error;
        },
    });

    // ─── Helpers UI ────────────────────────────────────────────────────────────
    function setField<K extends keyof ChatConfig>(key: K, value: ChatConfig[K]) {
        setDraft((prev) => ({ ...prev, [key]: value }));
        setDirtyKeys((prev) => new Set(prev).add(key));
    }

    async function persistField<K extends keyof ChatConfig>(key: K, value: ChatConfig[K], appConfigKey: string) {
        await upsertMutation.mutateAsync({ key: appConfigKey, value });
        setDirtyKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        queryClient.invalidateQueries({ queryKey: ['admin', 'chat-pescadillo-config'] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'app-config-all'] });
    }

    async function toggleEnabled() {
        const next = !draft.enabled;
        setField('enabled', next);
        await persistField('enabled', next, 'chat_pescadillo.enabled');
    }

    async function saveProvider(value: ProviderName) {
        setField('provider', value);
        await persistField('provider', value, 'chat_pescadillo.provider');
    }

    async function saveModelDeployment() {
        const trimmed = draft.modelDeployment.trim() || DEFAULTS.modelDeployment;
        setField('modelDeployment', trimmed);
        await persistField('modelDeployment', trimmed, 'chat_pescadillo.model_deployment');
    }

    async function saveNumeric<K extends 'dailyLimitMessages' | 'minuteLimitMessages' | 'maxHistoryMessages' | 'maxResponseTokens' | 'maxToolIterations'>(
        key: K,
        appConfigKey: string,
        min: number,
        max: number,
    ) {
        const clamped = Math.max(min, Math.min(max, Math.round(draft[key] as number)));
        setField(key, clamped as ChatConfig[K]);
        await persistField(key, clamped as ChatConfig[K], appConfigKey);
    }

    async function saveSystemPromptVersion() {
        const trimmed = draft.systemPromptVersion.trim() || DEFAULTS.systemPromptVersion;
        setField('systemPromptVersion', trimmed);
        await persistField('systemPromptVersion', trimmed, 'chat_pescadillo.system_prompt_version');
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const activeProviderHint = PROVIDER_OPTIONS.find((p) => p.value === draft.provider)?.hint ?? '';

    return (
        <div className="space-y-6">
            <PageHeader
                title="Chat Pescadillo (IA)"
                description="Configura el asistente IA Pro: estado, proveedor LLM, modelo, límites de uso y prompt."
            />

            {/* Estado del chat */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Estado del chat</h3>
                    </div>
                    <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
                        <div>
                            <p className="text-sm font-medium">
                                {draft.enabled ? 'Chat activo para usuarios Pro' : 'Chat deshabilitado'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                                {draft.enabled
                                    ? 'Los usuarios Pro pueden enviar mensajes (sujetos al rate limit).'
                                    : 'Toda petición devolverá 503 DISABLED hasta que actives el chat.'}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => void toggleEnabled()}
                            disabled={upsertMutation.isPending}
                            className={cn(
                                'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                draft.enabled
                                    ? 'bg-emerald-500/15 text-emerald-600 hover:bg-emerald-500/25'
                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            )}
                        >
                            {upsertMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : draft.enabled ? (
                                <><ToggleRight size={18} /> Desactivar</>
                            ) : (
                                <><ToggleLeft size={18} /> Activar</>
                            )}
                        </button>
                    </div>
                    <div
                        className={cn(
                            'flex items-center gap-3 p-4 rounded-lg',
                            draft.enabled
                                ? 'bg-emerald-500/10 border border-emerald-500/20'
                                : 'bg-amber-500/10 border border-amber-500/20'
                        )}
                    >
                        {draft.enabled ? (
                            <>
                                <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0" />
                                <p className="text-sm">El asistente está respondiendo a usuarios Pro en producción.</p>
                            </>
                        ) : (
                            <>
                                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                                <p className="text-sm">El chat está fuera de servicio. Cualquier petición será rechazada.</p>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Modelo / proveedor */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Proveedor y modelo</h3>
                    </div>

                    <div>
                        <p className="text-xs font-medium text-muted-foreground mb-2">Proveedor</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {PROVIDER_OPTIONS.map((opt) => {
                                const isActive = draft.provider === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => void saveProvider(opt.value)}
                                        disabled={upsertMutation.isPending}
                                        className={cn(
                                            'text-left p-4 rounded-lg border transition-colors',
                                            isActive
                                                ? 'border-primary bg-primary/10'
                                                : 'border-border bg-muted/20 hover:bg-muted/40'
                                        )}
                                    >
                                        <div className="flex items-center gap-2 mb-1">
                                            <Cpu size={14} className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                                            <span className="text-sm font-semibold">{opt.label}</span>
                                            {isActive && (
                                                <span className="ml-auto text-[10px] uppercase tracking-wide font-bold text-primary">
                                                    activo
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground">{opt.hint}</p>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">{activeProviderHint}</p>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-muted-foreground">Deployment / model id</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input
                                type="text"
                                value={draft.modelDeployment}
                                onChange={(e) => setField('modelDeployment', e.target.value)}
                                placeholder={DEFAULTS.modelDeployment}
                                className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
                            />
                            <button
                                type="button"
                                onClick={() => void saveModelDeployment()}
                                disabled={!dirtyKeys.has('modelDeployment') || upsertMutation.isPending}
                                className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                <Save size={14} /> Guardar
                            </button>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                            {MODEL_SUGGESTIONS[draft.provider].map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setField('modelDeployment', s)}
                                    className={cn(
                                        'px-2 py-0.5 rounded-md text-[10px] font-mono border',
                                        draft.modelDeployment === s
                                            ? 'border-primary text-primary bg-primary/10'
                                            : 'border-border text-muted-foreground hover:bg-muted'
                                    )}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Para Azure OpenAI, este es el <span className="font-semibold">nombre del deployment</span> creado en Azure AI Foundry, no el nombre del modelo base.
                        </p>
                    </div>
                </div>
            </div>

            {/* Límites */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Límites de uso</h3>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <NumericField
                            label="Mensajes por día (por usuario)"
                            value={draft.dailyLimitMessages}
                            min={1}
                            max={1000}
                            onChange={(v) => setField('dailyLimitMessages', v)}
                            onSave={() => saveNumeric('dailyLimitMessages', 'chat_pescadillo.daily_limit_messages', 1, 1000)}
                            dirty={dirtyKeys.has('dailyLimitMessages')}
                            saving={upsertMutation.isPending}
                            hint="Reset a las 00:00 UTC. 0 no está permitido (usa el toggle para deshabilitar)."
                        />
                        <NumericField
                            label="Mensajes por minuto (anti-spam)"
                            value={draft.minuteLimitMessages}
                            min={1}
                            max={60}
                            onChange={(v) => setField('minuteLimitMessages', v)}
                            onSave={() => saveNumeric('minuteLimitMessages', 'chat_pescadillo.minute_limit_messages', 1, 60)}
                            dirty={dirtyKeys.has('minuteLimitMessages')}
                            saving={upsertMutation.isPending}
                            hint="Equivale a 1 mensaje cada 60/N segundos. 5 → 1 cada 12s."
                        />
                        <NumericField
                            label="Historial enviado al modelo"
                            value={draft.maxHistoryMessages}
                            min={2}
                            max={50}
                            onChange={(v) => setField('maxHistoryMessages', v)}
                            onSave={() => saveNumeric('maxHistoryMessages', 'chat_pescadillo.max_history_messages', 2, 50)}
                            dirty={dirtyKeys.has('maxHistoryMessages')}
                            saving={upsertMutation.isPending}
                            hint="Nº de mensajes recientes que se envían como contexto."
                        />
                        <NumericField
                            label="Tokens máximos en respuesta"
                            value={draft.maxResponseTokens}
                            min={64}
                            max={4096}
                            onChange={(v) => setField('maxResponseTokens', v)}
                            onSave={() => saveNumeric('maxResponseTokens', 'chat_pescadillo.max_response_tokens', 64, 4096)}
                            dirty={dirtyKeys.has('maxResponseTokens')}
                            saving={upsertMutation.isPending}
                            hint="Acota la longitud de la respuesta del LLM."
                        />
                        <NumericField
                            label="Iteraciones de tool-calling"
                            value={draft.maxToolIterations}
                            min={1}
                            max={10}
                            onChange={(v) => setField('maxToolIterations', v)}
                            onSave={() => saveNumeric('maxToolIterations', 'chat_pescadillo.max_tool_iterations', 1, 10)}
                            dirty={dirtyKeys.has('maxToolIterations')}
                            saving={upsertMutation.isPending}
                            hint="Cuántas vueltas tool_use ↔ tool_result puede hacer el modelo en una respuesta."
                        />
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Versión del system prompt</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input
                                    type="text"
                                    value={draft.systemPromptVersion}
                                    onChange={(e) => setField('systemPromptVersion', e.target.value)}
                                    placeholder={DEFAULTS.systemPromptVersion}
                                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
                                />
                                <button
                                    type="button"
                                    onClick={() => void saveSystemPromptVersion()}
                                    disabled={!dirtyKeys.has('systemPromptVersion') || upsertMutation.isPending}
                                    className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                                >
                                    <Save size={14} /> Guardar
                                </button>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Informativo. Para cambiar el contenido del prompt edita
                                <span className="font-mono ml-1">azure-functions/services/chat-pescadillo/system-prompt.ts</span>.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Uso 7 días */}
            <div className="rounded-xl border border-border bg-card overflow-hidden">
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">Uso últimos 7 días</h3>
                    </div>
                    {usageLoading ? (
                        <div className="flex justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : usage.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4">Sin actividad registrada todavía.</p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <KpiCard label="Hoy · usuarios" value={String(totals.todayUsers)} />
                                <KpiCard label="Hoy · mensajes" value={String(totals.todayMessages)} />
                                <KpiCard label="7d · mensajes" value={String(totals.messages)} />
                                <KpiCard
                                    label="7d · tokens (in / out)"
                                    value={`${totals.tokensIn.toLocaleString('es-ES')} / ${totals.tokensOut.toLocaleString('es-ES')}`}
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="text-left">
                                        <tr className="text-xs uppercase text-muted-foreground border-b border-border">
                                            <th className="py-2">Día</th>
                                            <th className="py-2">Usuarios</th>
                                            <th className="py-2">Mensajes</th>
                                            <th className="py-2">Tokens in / out</th>
                                            <th className="py-2">Provider</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usage.map((row) => (
                                            <tr key={row.day_utc} className="border-b border-border/40">
                                                <td className="py-2 font-mono text-xs">{row.day_utc}</td>
                                                <td className="py-2">{row.active_users}</td>
                                                <td className="py-2">{row.total_messages}</td>
                                                <td className="py-2 font-mono text-xs">
                                                    {Number(row.total_tokens_in).toLocaleString('es-ES')}
                                                    {' / '}
                                                    {Number(row.total_tokens_out).toLocaleString('es-ES')}
                                                </td>
                                                <td className="py-2 text-xs">
                                                    {Object.entries(row.by_provider ?? {})
                                                        .map(([p, v]) => `${p}: ${v.messages}`)
                                                        .join(' · ') || <span className="text-muted-foreground">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─── Subcomponentes ────────────────────────────────────────────────────────────

interface NumericFieldProps {
    label: string;
    value: number;
    min: number;
    max: number;
    onChange: (v: number) => void;
    onSave: () => void | Promise<void>;
    dirty: boolean;
    saving: boolean;
    hint?: string;
}

function NumericField({ label, value, min, max, onChange, onSave, dirty, saving, hint }: NumericFieldProps) {
    return (
        <div>
            <label className="text-xs font-medium text-muted-foreground">{label}</label>
            <div className="flex items-center gap-2 mt-1">
                <input
                    type="number"
                    value={value}
                    min={min}
                    max={max}
                    onChange={(e) => onChange(Number(e.target.value))}
                    className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono"
                />
                <button
                    type="button"
                    onClick={() => void onSave()}
                    disabled={!dirty || saving}
                    className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar
                </button>
            </div>
            {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
}

function KpiCard({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg border border-border bg-muted/30 p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
            <p className="text-xl font-bold text-foreground mt-1">{value}</p>
        </div>
    );
}
